import React, { useEffect, useState, useRef } from 'react';
import { IconX, IconMicrophone, IconPlayerStop, IconWaveSine } from '@tabler/icons-react';
interface VoiceConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTranscription?: (text: string) => void;
}
type ConversationState = 'idle' | 'listening' | 'processing' | 'speaking';
export function VoiceConversationModal({
  isOpen,
  onClose,
  onTranscription
}: VoiceConversationModalProps) {
  const [state, setState] = useState<ConversationState>('idle');
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [pulseScale, setPulseScale] = useState(1);
  const animationRef = useRef<number>();
  // Simulated voice animation
  useEffect(() => {
    if (state === 'listening' || state === 'speaking') {
      const animate = () => {
        const time = Date.now() / 200;
        const scale = 1 + Math.sin(time) * 0.15 + Math.random() * 0.1;
        setPulseScale(scale);
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    } else {
      setPulseScale(1);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state]);
  // Auto-start listening when modal opens
  useEffect(() => {
    if (isOpen) {
      setState('listening');
      setTranscript('');
      setAiResponse('');
      // Simulate listening for 3 seconds then processing
      const listenTimer = setTimeout(() => {
        setTranscript('Come posso organizzare meglio la giornata di Sofia?');
        setState('processing');
        // Simulate processing for 1.5 seconds
        const processTimer = setTimeout(() => {
          setAiResponse('Certo! Posso aiutarti a creare una routine giornaliera per Sofia. Vuoi che iniziamo dalla mattina o preferisci concentrarti su un momento specifico della giornata?');
          setState('speaking');
          // Simulate speaking for 4 seconds then back to listening
          const speakTimer = setTimeout(() => {
            setState('listening');
            setTranscript('');
          }, 4000);
          return () => clearTimeout(speakTimer);
        }, 1500);
        return () => clearTimeout(processTimer);
      }, 3000);
      return () => clearTimeout(listenTimer);
    }
  }, [isOpen]);
  const handleClose = () => {
    setState('idle');
    setTranscript('');
    setAiResponse('');
    onClose();
  };
  const handleStopListening = () => {
    if (state === 'listening') {
      setState('processing');
      // Simulate quick processing
      setTimeout(() => {
        setAiResponse("Ho capito. C'è qualcos'altro in cui posso aiutarti?");
        setState('speaking');
      }, 1000);
    }
  };
  const getStateText = () => {
    switch (state) {
      case 'listening':
        return 'Ti ascolto...';
      case 'processing':
        return 'Sto pensando...';
      case 'speaking':
        return 'Yeppy sta parlando';
      default:
        return 'Premi per parlare';
    }
  };
  const getStateColor = () => {
    switch (state) {
      case 'listening':
        return '#B0EC9C';
      // Green
      case 'processing':
        return '#FAD88A';
      // Yellow
      case 'speaking':
        return '#AEDAFF';
      // Blue
      default:
        return '#1D2A36';
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-390">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-yeppy-text bg-opacity-95" onClick={handleClose} data-id="element-391" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-6 py-12" data-id="element-392">
        {/* Close button */}
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-yeppy-bg text-yeppy-text transition-colors" data-id="element-393">
          <IconX className="w-5 h-5" stroke={1.5} data-id="element-394" />
        </button>

        {/* Logo */}
        <div className="absolute top-6 left-6" data-id="element-395">
          <img src="https://cdn.magicpatterns.com/uploads/bgucnc6iAv4SE4noNyVpoM/Logo_esteso.svg" alt="Yeppy" className="h-6 brightness-0 invert opacity-60" data-id="element-396" />
        </div>

        {/* Main voice orb */}
        <div className="flex-1 flex flex-col items-center justify-center" data-id="element-397">
          {/* Animated orb */}
          <div className="relative mb-8" data-id="element-398">
            {/* Outer glow rings */}
            <div className="absolute inset-0 rounded-full opacity-20 blur-xl transition-transform duration-100" style={{
            backgroundColor: getStateColor(),
            transform: `scale(${pulseScale * 1.5})`
          }} data-id="element-399" />
            <div className="absolute inset-0 rounded-full opacity-30 blur-md transition-transform duration-100" style={{
            backgroundColor: getStateColor(),
            transform: `scale(${pulseScale * 1.2})`
          }} data-id="element-400" />

            {/* Main orb */}
            <div className="relative w-40 h-40 rounded-full flex items-center justify-center transition-transform duration-100" style={{
            backgroundColor: getStateColor(),
            transform: `scale(${pulseScale})`,
            boxShadow: `0 0 60px ${getStateColor()}40`
          }} data-id="element-401">
              {state === 'listening' ? <div className="w-24 h-24 rounded-full bg-yeppy-green bg-opacity-20 flex items-center justify-center animate-pulse" data-id="element-402">
                  <div className="w-16 h-16 rounded-full bg-yeppy-green flex items-center justify-center text-yeppy-text" data-id="element-403">
                    <IconMicrophone className="w-8 h-8" stroke={1.5} data-id="element-404" />
                  </div>
                </div> : state === 'processing' ? <div className="flex gap-2" data-id="element-405">
                  <div className="w-3 h-3 rounded-full bg-yeppy-text animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-406" />
                  <div className="w-3 h-3 rounded-full bg-yeppy-text animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-407" />
                  <div className="w-3 h-3 rounded-full bg-yeppy-text animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-408" />
                </div> : <div className="w-24 h-24 rounded-full bg-yeppy-blue bg-opacity-20 flex items-center justify-center" data-id="element-409">
                  <div className="w-16 h-16 rounded-full bg-yeppy-blue flex items-center justify-center text-yeppy-text" data-id="element-410">
                    <IconWaveSine className="w-8 h-8 animate-pulse" stroke={1.5} data-id="element-411" />
                  </div>
                </div>}
            </div>
          </div>

          {/* State text */}
          <p className="text-white text-xl mb-4" data-id="element-412">{getStateText()}</p>

          {/* Transcript or AI response */}
          <div className="max-w-md text-center min-h-[80px]" data-id="element-413">
            {state === 'listening' && transcript && <p className="text-white text-opacity-80 text-base leading-relaxed animate-fade-in" data-id="element-414">
                "{transcript}"
              </p>}
            {state === 'processing' && transcript && <p className="text-white text-opacity-60 text-base leading-relaxed" data-id="element-415">
                "{transcript}"
              </p>}
            {state === 'speaking' && aiResponse && <p className="text-white text-opacity-90 text-base leading-relaxed animate-fade-in" data-id="element-416">
                {aiResponse}
              </p>}
          </div>
        </div>

        {/* Bottom controls */}
        <div className="flex flex-col items-center gap-4" data-id="element-417">
          {state === 'listening' && <button onClick={handleStopListening} className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center transition-colors" aria-label="Ferma registrazione" data-id="element-418">
              <IconPlayerStop className="w-8 h-8 text-white" stroke={1.5} data-id="element-419" />
            </button>}

          <p className="text-white text-opacity-50 text-sm" data-id="element-420">
            Tocca fuori per chiudere
          </p>
        </div>
      </div>

      <style jsx data-id="element-421">{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>;
}